/**
 * Matérialisation du plan d'une séance, depuis une routine
 * (`ARCHITECTURE_V2.md` §4.3 et §4.5).
 *
 * 🔴 **Une fonction pure, et c'est ce qui rend la séance composable hors
 * ligne.** Démarrer une séance en salle ne demande **aucun aller-retour** : la
 * routine est déjà dans la réplique (§3.1), le client tire les identités
 * lui-même (§2.3-b), et le plan se déroule ici. Le serveur n'apprend
 * l'existence de la séance qu'au drainage, ce qui peut être trois heures plus
 * tard.
 *
 * 🔴 **Le nombre de séries produites est exactement celui qu'annonce
 * `routineWeight()`** — `line.sets × tours`, `tours` valant `rounds` pour un
 * circuit et 1 ailleurs. Ce n'est pas une coïncidence à entretenir : c'est un
 * invariant. Le détail d'une routine affiche « 21 séries » avant qu'on la
 * lance ; si le plan en produisait 18, le résumé mentirait sur la séance qu'il
 * décrit, et personne ne signalerait le défaut — on se dirait qu'on avait mal
 * lu. Même raisonnement que `shared/search.ts` à l'étape 4 : deux calculs de la
 * même grandeur finissent par diverger.
 *
 * ⚠️ **Rien ici ne parle au réseau, et rien n'écrit.** Les identités sont
 * tirées par l'appelant (`newClientId`) et passées en paramètre, de sorte que
 * la fonction reste rejouable à l'identique — un test peut lui donner un
 * compteur, l'écran lui donne `crypto.randomUUID()`.
 */

import type { ActualSetInput, RoutineBlockDTO, RoutineDTO, RoutineLineDTO } from '../../../../shared/types';

/**
 * Une série planifiée : son adresse, et ce qu'elle vaut au serveur.
 *
 * ⚠️ **`clientId` est porté à côté du corps, pas dedans** — c'est l'adresse de
 * la ressource (`PUT /api/sessions/:c/sets/:sc`), donc elle vit dans le chemin.
 * Exactement comme `RoutineInput` ne porte pas le `clientId` de la routine.
 */
export interface PlannedSet {
  clientId: string;
  body: ActualSetInput;
  /**
   * La vignette de l'exercice, **portée à côté du corps**.
   *
   * 🔴 **Elle ne peut pas voyager DANS le corps** : les routes d'écriture
   * refusent `mediaKey` et `imageCount` par construction (§4.1 — laisser un
   * corps de requête les fixer reviendrait à laisser le client désigner un
   * chemin sur le disque du serveur). Mais l'**aperçu optimiste** en a besoin,
   * sans quoi les vignettes n'apparaissent qu'à la première réponse du serveur
   * — c'est-à-dire, hors ligne, **jamais**.
   *
   * ⚠️ Défaut trouvé à l'usage *(29/08/2026)* : « les vignettes ne s'affichent
   * qu'après avoir validé la première série ». `RoutineLineDTO` porte
   * `exerciseMediaKey` et `exerciseImageCount` **exactement pour ça** depuis
   * l'étape 5 ; on ne s'en servait pas.
   */
  media: { mediaKey: string | null; imageCount: number };
}

/**
 * Le repos qui suit cette série, en secondes — `null` s'il n'y en a pas.
 *
 * 🔴 **C'est une propriété du BLOC, jamais de la ligne, sauf en `single`**
 * (§4.3). Le serveur force déjà `routine_line.rest_seconds` à `null` hors d'un
 * bloc `single` ; cette fonction lit donc la seule valeur qui puisse exister,
 * et il n'y a pas deux vérités à départager.
 *
 * ⚠️ **Dans un superset, valider la première série ne déclenche AUCUN repos**
 * (§4.3) — « on enchaîne une série de chaque ligne sans repos, puis le repos du
 * bloc ». C'est la seule lecture qui corresponde à ce qu'on fait en salle, et
 * c'est `restAfter` qui la porte : le repos ne tombe qu'à la **dernière ligne**
 * du tour.
 */
export function restAfter(
  block: RoutineBlockDTO,
  line: RoutineLineDTO,
  derniereDuTour: boolean,
): number | null {
  if (block.kind === 'single') return line.restSeconds ?? block.restSeconds;
  return derniereDuTour ? block.restSeconds : null;
}

/**
 * Nombre de tours d'un bloc — `rounds` pour un circuit, 1 partout ailleurs.
 *
 * ⚠️ **Un superset ignore `rounds`** (§4.3 : « le nombre de tours est celui des
 * séries »), et le serveur le force déjà à `null`. Le lire quand même serait
 * lire une valeur qu'on a promis d'ignorer.
 */
function toursOf(block: RoutineBlockDTO): number {
  return block.kind === 'circuit' ? (block.rounds ?? 1) : 1;
}

/**
 * Les cibles copiées du plan sur la série (§4.2).
 *
 * 🔴 **Copiées, jamais jointes.** C'est ce qui permet de relire « j'avais visé
 * 4×8 à 60 kg, j'ai fait 8, 8, 7, 6 » des mois plus tard, alors que la routine
 * a évolué entre-temps.
 *
 * ⚠️ **Exactement une mesure**, reps **ou** durée — la ligne l'a déjà garanti
 * (`measure_required`, migration `0010`), on la recopie telle quelle sans rien
 * convertir. Convertir serait réinterpréter le modèle.
 */
function targetsOf(line: RoutineLineDTO) {
  return {
    targetRepsMin: line.repMin,
    targetRepsMax: line.repMax,
    targetDurationSeconds: line.durationSeconds,
    targetWeightKg: line.targetWeightKg,
    targetRpe: line.targetRpe,
  };
}

/**
 * Déroule une routine en séries planifiées, dans l'ordre où on les fera.
 *
 * L'ordre est celui du §4.3, et il diffère par nature de bloc :
 *
 * | `kind`     | Ordre produit |
 * |------------|---------------|
 * | `single`   | les séries de la ligne à la suite |
 * | `superset` | tour 1 : une série de **chaque** ligne ; tour 2 : idem… |
 * | `circuit`  | `rounds` tours, chaque tour reprenant chaque ligne |
 *
 * ⚠️ **Le superset s'entrelace, le `single` non**, et c'est tout ce qui les
 * distingue à l'exécution — d'où le refus `single_has_many` côté serveur : un
 * `single` à plusieurs lignes n'aurait pas d'ordre défini, et cette fonction
 * devrait en inventer un.
 *
 * ⚠️ **Le nombre de tours d'un superset est le plus grand `sets` de ses
 * lignes.** Une ligne qui en a moins cesse simplement d'apparaître dans les
 * derniers tours — plutôt que d'être complétée par des séries que personne n'a
 * demandées, ou de tronquer les autres.
 *
 * ⚠️ **`setIndex` compte les séries de la ligne, tours confondus** : dans un
 * circuit de 4 tours sur une ligne à 3 séries, la dernière est la 12ᵉ de cette
 * ligne et le dit. Compter par tour afficherait quatre fois « série 3 sur 3 ».
 */
export function planOfRoutine(routine: RoutineDTO, nextId: () => string): PlannedSet[] {
  const plan: PlannedSet[] = [];
  let position = 0;

  for (const block of routine.blocks) {
    const tours = toursOf(block);
    /* Compteur par ligne — cf. l'en-tête : `setIndex` ignore les tours. */
    const rang = new Map<string, number>();

    if (block.kind === 'single') {
      const line = block.lines[0];
      if (!line) continue;
      for (let s = 0; s < line.sets; s += 1) {
        plan.push(plannedSet(block, line, position, s + 1, nextId));
        position += 1;
      }
      continue;
    }

    /*
     * Superset et circuit s'entrelacent tous deux ; seul le nombre de passages
     * change. Pour un superset, le §4.3 dit que le nombre de tours est celui
     * des séries — d'où `max(sets)` ci-dessous et `tours = 1`.
     */
    const passages = block.kind === 'superset' ? Math.max(...block.lines.map((l) => l.sets), 0) : 1;

    for (let tour = 0; tour < tours; tour += 1) {
      for (let passage = 0; passage < passages; passage += 1) {
        for (const line of block.lines) {
          /* Une ligne plus courte que les autres sort des derniers tours. */
          if (block.kind === 'superset' && passage >= line.sets) continue;
          /*
           * Dans un circuit, une ligne qui déclare plusieurs séries les fait à
           * la suite dans le tour — le cas courant reste `sets: 1`, où l'on
           * retrouve exactement « un tour = une série de chaque ligne ».
           */
          const parTour = block.kind === 'circuit' ? line.sets : 1;
          for (let s = 0; s < parTour; s += 1) {
            const suivant = (rang.get(line.clientId) ?? 0) + 1;
            rang.set(line.clientId, suivant);
            plan.push(plannedSet(block, line, position, suivant, nextId));
            position += 1;
          }
        }
      }
    }
  }

  return plan;
}

function plannedSet(
  block: RoutineBlockDTO,
  line: RoutineLineDTO,
  position: number,
  setIndex: number,
  nextId: () => string,
): PlannedSet {
  return {
    clientId: nextId(),
    media: { mediaKey: line.exerciseMediaKey, imageCount: line.exerciseImageCount },
    body: {
      exerciseClientId: line.exerciseClientId,
      /*
       * ⚠️ **Le nom est copié dès le plan**, alors même que le serveur le
       * réécrira depuis l'exercice vivant. C'est le repli du §4.2 : si
       * l'exercice disparaît entre la composition hors ligne et le drainage,
       * c'est cette valeur qui sauve la série d'un refus 400 — donc d'une
       * disparition pure et simple (§3.3, un 4xx est `failed` définitif).
       */
      exerciseName: line.exerciseName,
      /* Copie du `clientId` du bloc — regroupe le superset à l'affichage. */
      blockKey: block.clientId,
      position,
      setIndex,
      setKind: line.setKind,
      status: 'pending',
      ...targetsOf(line),
    },
  };
}

/**
 * Nombre de séries que produira `planOfRoutine`, sans la dérouler.
 *
 * ⚠️ **Le même calcul que `routineWeight().sets`**, et volontairement écrit
 * ici aussi : c'est ce qui permet au banc de vérifier l'invariant de l'en-tête
 * sans instancier d'identités. S'il faut un jour changer l'un, les deux
 * doivent bouger ensemble — et l'écart se voit immédiatement.
 */
export function plannedSetCount(routine: RoutineDTO): number {
  let total = 0;
  for (const block of routine.blocks) {
    const tours = toursOf(block);
    for (const line of block.lines) total += line.sets * tours;
  }
  return total;
}
